import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const PAGE = {
  width: 210,
  height: 297,
  marginLeft: 12,
  marginRight: 12,
  marginTop: 16,
  marginBottom: 16,
};

const MODULE_ORDER = ["Section A", "Section B", "Section C"];
const MODULE_DISPLAY = {
  "Section A": "SECTION A: GENERAL DISCLOSURES",
  "Section B": "SECTION B: MANAGEMENT AND PROCESS DISCLOSURES",
  "Section C": "SECTION C: PRINCIPLE-WISE PERFORMANCE DISCLOSURE",
};
const COVER_IMAGE_SRC = "/assets/img/preview-report-01.jpg";
const THEME = {
  brand: [64, 119, 117],
  dark: [45, 63, 80],
  text: [75, 85, 99],
  muted: [107, 114, 128],
  /** Sub-part marker (a., (i)) and its question text — same color, distinct from main Q number and answers. */
  subQuestionLabel: [100, 116, 139], // #64748b
  tableHeaderBg: [239, 246, 245], // #eff6f5 — matches Section A/B .section-a-dynamic-table-header
  /** Section C thead (main headers) — same as Section A/B table headers */
  tableHeaderGreen: [239, 246, 245],
  sectionRowBg: [242, 242, 242],
  latestFyBg: [255, 247, 204],
  latestFyText: [167, 120, 32],
  tableLine: [214, 224, 221],
  pageBg: [248, 250, 252],
  questionTypeBg: [240, 247, 247], // #f0f7f7 — matches prior react-pdf questionTypeTitle
};

/** Normalize whitespace; map glyphs missing from PDF standard fonts (e.g. ₹) to ASCII. */
const sanitizeText = (value) =>
  String(value || "")
    .replace(/\u20B9/g, "Rs.")
    .replace(/\uFF05/g, "%")
    .replace(/\u00A0/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Like sanitizeText but keeps newline boundaries for multi-line answers. */
const pdfSafeMultiline = (value) =>
  String(value ?? "")
    .replace(/\u20B9/g, "Rs.")
    .replace(/\uFF05/g, "%")
    .replace(/\u00A0/g, " ");

const stripHtmlToText = (htmlString) => {
  if (!htmlString || typeof htmlString !== "string") return "";
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  return sanitizeText(doc.body?.textContent || "");
};

const hasTable = (htmlString) => typeof htmlString === "string" && /<table[\s\S]*?>/i.test(htmlString);

const hasBrsrPdfRichLines = (htmlString) => typeof htmlString === "string" && /class\s*=\s*["'][^"']*brsr-pdf-rich-lines/i.test(htmlString);

const splitHtmlFragments = (htmlString) => {
  if (!htmlString || typeof htmlString !== "string") return [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div id="root">${htmlString}</div>`, "text/html");
  const root = doc.querySelector("#root");
  if (!root) return [];

  const fragments = [];
  const pushText = (text) => {
    const cleaned = sanitizeText(text);
    if (cleaned) fragments.push({ type: "text", content: cleaned });
  };

  Array.from(root.childNodes).forEach((node) => {
    if (node.nodeType === 3) {
      pushText(node.textContent || "");
      return;
    }

    if (node.nodeType !== 1) return;
    const el = node;

    if (el.tagName === "TABLE") {
      fragments.push({ type: "table", element: el });
      return;
    }

    const nestedTables = Array.from(el.querySelectorAll(":scope > table"));
    if (nestedTables.length === 0) {
      pushText(el.textContent || "");
      return;
    }

    Array.from(el.childNodes).forEach((child) => {
      if (child.nodeType === 3) {
        pushText(child.textContent || "");
        return;
      }
      if (child.nodeType !== 1) return;
      const childEl = child;
      if (childEl.tagName === "TABLE") {
        fragments.push({ type: "table", element: childEl });
      } else {
        pushText(childEl.textContent || "");
      }
    });
  });

  return fragments;
};

/** Section B per-principle PDF: structured rows for custom layout (fixed label column + wrapped description). */
const extractBrsrPerPrincipleRowsFromCell = (cell) => {
  const table = cell.querySelector(":scope > table.brsr-per-principle-pdf-table");
  if (!table) return null;
  const tbody = table.querySelector("tbody");
  const rowParent = tbody || table;
  const rows = Array.from(rowParent.children).filter((n) => n.tagName === "TR");
  if (rows.length === 0) return null;
  const out = [];
  for (const tr of rows) {
    const cells = Array.from(tr.children).filter((c) => c.tagName === "TD" || c.tagName === "TH");
    if (cells.length !== 2) return null;
    out.push({
      label: sanitizeText(cells[0].textContent || ""),
      text: sanitizeText(cells[1].textContent || "") || "-",
    });
  }
  return out;
};

/** Section B per-principle: inner table is one row per P with exactly two cells — format as lines instead of mashed textContent. */
const tryTwoColumnNestedTableAsLines = (cell) => {
  if (cell.querySelector(":scope > table.brsr-per-principle-pdf-table")) return null;
  const table = cell.querySelector(":scope > table");
  if (!table) return null;
  const tbody = table.querySelector("tbody");
  const rowParent = tbody || table;
  const rows = Array.from(rowParent.children).filter((n) => n.tagName === "TR");
  if (rows.length === 0) return null;
  for (const tr of rows) {
    const cells = Array.from(tr.children).filter((c) => c.tagName === "TD" || c.tagName === "TH");
    if (cells.length !== 2) return null;
  }
  return rows
    .map((tr) => {
      const cells = Array.from(tr.children).filter((c) => c.tagName === "TD" || c.tagName === "TH");
      const left = sanitizeText(cells[0].textContent || "");
      const right = sanitizeText(cells[1].textContent || "") || "-";
      return `${left}   ${right}`.trim();
    })
    .join("\n");
};

const cellTextWithLineBreaks = (cell) => {
  try {
    const nestedLines = tryTwoColumnNestedTableAsLines(cell);
    if (nestedLines != null && nestedLines !== "") {
      return nestedLines;
    }
    const clone = cell.cloneNode(true);
    clone.querySelectorAll("br").forEach((br) => {
      br.replaceWith("\n");
    });
    const t = sanitizeText(clone.textContent || "");
    return t || "-";
  } catch {
    return sanitizeText(cell.textContent || "-") || "-";
  }
};

const parseTableRows = (rows, isHeader = false) => {
  return Array.from(rows).map((tr) =>
    Array.from(tr.children)
      .filter((cell) => cell.tagName === "TD" || cell.tagName === "TH")
      .map((cell) => {
        const colSpan = Math.max(1, parseInt(cell.getAttribute("colspan") || "1", 10));
        const rowSpan = Math.max(1, parseInt(cell.getAttribute("rowspan") || "1", 10));
        const trEl = cell.closest?.("tr");
        const trClass = trEl?.getAttribute?.("class") || "";
        const _brsrSectionBHeaderRow = /\bbrsr-sb-q10-header-row\b/.test(trClass);
        const _isThCell = cell.tagName === "TH";
        const brsrRows = extractBrsrPerPrincipleRowsFromCell(cell);
        if (brsrRows && brsrRows.length > 0) {
          return {
            content: " ",
            colSpan,
            rowSpan,
            _brsrPerPrincipleRows: brsrRows,
            _brsrSectionBHeaderRow,
            _isThCell,
            styles: {
              fontStyle: isHeader || _isThCell ? "bold" : "normal",
              halign: "left",
              valign: "top",
            },
          };
        }
        const content = cellTextWithLineBreaks(cell);
        return {
          content,
          colSpan,
          rowSpan,
          _brsrSectionBHeaderRow,
          _isThCell,
          styles: {
            fontStyle: isHeader || _isThCell ? "bold" : "normal",
            halign: "left",
            valign: "middle",
          },
        };
      }),
  );
};

/**
 * Section B per-principle: one HTML cell with P1–P9 maps to one AutoTable row with a very tall merged cell.
 * jsPDF-AutoTable splits oversized cells across pages, but `willDrawCell` still receives the full cell data and
 * redraws P1–P9 from the top on every fragment — continuation pages show repeated/wrong principles. Emit one
 * body row per principle only for that answer row. The disclosure question column uses `rowSpan` so the label
 * is merged vertically (not repeated). When two cells are per-principle (e.g. Q10 review + frequency), rows are
 * zipped by index; continuation rows omit leading cells covered by rowspan.
 */
const expandBrsrPerPrincipleBodyRows = (bodyRows) => {
  const out = [];
  for (const row of bodyRows || []) {
    const multiIdx = [];
    row.forEach((c, j) => {
      if (Array.isArray(c?._brsrPerPrincipleRows) && c._brsrPerPrincipleRows.length > 1) {
        multiIdx.push(j);
      }
    });
    if (multiIdx.length === 0) {
      out.push(row);
      continue;
    }
    const n = row[multiIdx[0]]._brsrPerPrincipleRows.length;
    if (!multiIdx.every((j) => row[j]._brsrPerPrincipleRows.length === n)) {
      out.push(row);
      continue;
    }
    const firstMulti = multiIdx[0];

    const tagPpSlice = (cell, i) => ({
      ...cell,
      content: " ",
      _brsrPerPrincipleRows: [cell._brsrPerPrincipleRows[i]],
      _brsrPPBlockIndex: i,
      _brsrPPBlockSize: n,
    });

    const buildSliceForIndex = (i) => {
      const sliceRow = [];
      for (let j = firstMulti; j < row.length; j++) {
        const cell = row[j];
        if (multiIdx.includes(j)) {
          sliceRow.push(tagPpSlice(cell, i));
        } else {
          sliceRow.push({ ...cell });
        }
      }
      return sliceRow;
    };

    for (let i = 0; i < n; i++) {
      if (i === 0) {
        const firstRow = row.map((cell, j) => {
          if (j < firstMulti) {
            return { ...cell, rowSpan: n, _brsrPPBlockLead: true, _brsrPPBlockSize: n };
          }
          if (multiIdx.includes(j)) {
            return tagPpSlice(cell, i);
          }
          return { ...cell };
        });
        out.push(firstRow);
      } else {
        out.push(buildSliceForIndex(i));
      }
    }
  }
  return out;
};

/** Direct `<tr>` children only — omits rows inside nested tables (per-principle blocks, Q10, etc.). */
const directTrChildren = (sectionEl) => Array.from(sectionEl?.children || []).filter((n) => n && n.tagName === "TR");

const tableElementToAutoTableInput = (tableElement) => {
  const thead = tableElement.querySelector("thead");
  const tbody = tableElement.querySelector("tbody");

  let headRows = [];
  let bodyRows = [];

  if (thead) {
    headRows = parseTableRows(directTrChildren(thead), true);
  }

  if (tbody) {
    bodyRows = parseTableRows(directTrChildren(tbody), false);
  } else {
    const fallbackRows = [];
    for (const child of tableElement.children) {
      if (child.tagName === "TBODY" || child.tagName === "TFOOT") {
        fallbackRows.push(...directTrChildren(child));
      }
    }
    if (fallbackRows.length === 0) {
      fallbackRows.push(...Array.from(tableElement.children).filter((n) => n.tagName === "TR"));
    }
    if (headRows.length === 0 && fallbackRows.length > 0) {
      headRows = parseTableRows([fallbackRows[0]], true);
      bodyRows = parseTableRows(fallbackRows.slice(1), false);
    } else {
      bodyRows = parseTableRows(fallbackRows, false);
    }
  }

  if (headRows.length === 0 && bodyRows.length > 0) {
    headRows = [bodyRows.shift()];
  }

  return { headRows, bodyRows };
};

const expandRowByColspan = (row) => {
  const expanded = [];
  (row || []).forEach((cell) => {
    const span = Math.max(1, cell?.colSpan || 1);
    for (let i = 0; i < span; i++) {
      expanded.push(cell?.content || "");
    }
  });
  return expanded;
};

const detectNumericColumns = (headRows, bodyRows) => {
  const sampleRows = (bodyRows || []).map(expandRowByColspan).filter((r) => r.length > 0);
  const headExpanded = (headRows || []).map(expandRowByColspan);
  const maxCols = Math.max(
    headExpanded.reduce((m, r) => Math.max(m, r.length), 0),
    sampleRows.reduce((m, r) => Math.max(m, r.length), 0),
  );
  const numericCols = new Set();

  if (maxCols === 0) return numericCols;

  for (let col = 0; col < maxCols; col++) {
    let seen = 0;
    let numeric = 0;
    for (let r = 0; r < sampleRows.length && r < 20; r++) {
      const txt = sanitizeText(sampleRows[r][col] || "");
      if (!txt || txt === "-") continue;
      seen++;
      if (/^[\d,.\-+()%]+$/.test(txt)) {
        numeric++;
      }
    }
    if (seen > 0 && numeric / seen >= 0.7) {
      numericCols.add(col);
    }
  }
  return numericCols;
};

const getCellColumnStart = (row, targetCellIdx) => {
  let cursor = 0;
  for (let i = 0; i < targetCellIdx; i++) {
    const span = Math.max(1, row?.[i]?.colSpan || 1);
    cursor += span;
  }
  return cursor;
};

/**
 * Classify a header cell as FY-related (latest vs past vs numeric year).
 * Scans work across all thead rows via getLatestFyColumnRange.
 */
const classifyFyHeaderCell = (text) => {
  const t = sanitizeText(text || "");
  if (!t || t === "-") return null;
  if (/_{3,}/.test(t) && !/[12]\d{3}/.test(t)) return null;

  if (/(current\s*(financial\s*)?year|current\s*fy|latest\s*fy)/i.test(t)) {
    return { kind: "current" };
  }
  if (/(previous\s*(financial\s*)?year|previous\s*fy|prior\s*fy|older\s*fy)/i.test(t)) {
    return { kind: "past" };
  }

  let m = t.match(/(?:^|\s)fy\s*([12]\d{3})\s*[-–]\s*(\d{2,4})/i);
  if (!m) m = t.match(/^([12]\d{3})\s*[-–]\s*(\d{2,4})$/);
  if (m) {
    const y = parseInt(m[1], 10);
    if (y >= 1990 && y <= 2100) return { kind: "year", year: y };
  }
  return null;
};

/**
 * Place thead cells on a grid (respect rowspan/colspan) so FY column indices match body rows.
 */
const annotateHeadCells = (headRows) => {
  const result = [];
  const rows = headRows.length;
  const maxCols = 48;
  const occ = Array.from({ length: rows }, () => Array(maxCols).fill(false));

  headRows.forEach((row, ri) => {
    let c = 0;
    (row || []).forEach((cell) => {
      while (c < maxCols && occ[ri][c]) c++;
      const cs = Math.max(1, cell?.colSpan || 1);
      const rs = Math.max(1, cell?.rowSpan || 1);
      for (let dr = 0; dr < rs; dr++) {
        for (let dc = 0; dc < cs; dc++) {
          if (ri + dr < rows) occ[ri + dr][c + dc] = true;
        }
      }
      result.push({
        rowIdx: ri,
        startCol: c,
        endCol: c + cs - 1,
        content: cell?.content || "",
        cell,
      });
      c += cs;
    });
  });
  return result;
};

/**
 * Latest FY column range for multi-year tables only.
 * Uses grid-aligned header positions so rowspan/colspan (e.g. P2 Leadership Q3) does not shift highlights.
 */
const getLatestFyColumnRange = (headRows) => {
  if (!Array.isArray(headRows) || headRows.length === 0) return null;

  const annotated = annotateHeadCells(headRows);
  const segments = [];
  annotated.forEach(({ startCol, endCol, content }) => {
    const classification = classifyFyHeaderCell(content || "");
    if (classification) {
      segments.push({
        start: startCol,
        end: endCol,
        ...classification,
      });
    }
  });

  if (segments.length === 0) return null;

  const currentBlocks = segments.filter((s) => s.kind === "current");
  if (currentBlocks.length > 0) {
    const ranges = currentBlocks.map((b) => ({ start: b.start, end: b.end }));
    // Merge overlapping/adjacent ranges to avoid "min/max bleed" into past columns.
    const sorted = ranges.sort((a, b) => a.start - b.start);
    const merged = [];
    sorted.forEach((r) => {
      const last = merged[merged.length - 1];
      if (!last || r.start > last.end + 1) merged.push({ ...r });
      else last.end = Math.max(last.end, r.end);
    });
    return { ranges: merged };
  }

  const yearBlocks = segments.filter((s) => s.kind === "year" && typeof s.year === "number");
  const pastBlocks = segments.filter((s) => s.kind === "past");

  if (yearBlocks.length >= 2) {
    const maxYear = Math.max(...yearBlocks.map((b) => b.year));
    const winners = yearBlocks.filter((b) => b.year === maxYear);
    const ranges = winners.map((b) => ({ start: b.start, end: b.end }));
    const sorted = ranges.sort((a, b) => a.start - b.start);
    const merged = [];
    sorted.forEach((r) => {
      const last = merged[merged.length - 1];
      if (!last || r.start > last.end + 1) merged.push({ ...r });
      else last.end = Math.max(last.end, r.end);
    });
    return { ranges: merged };
  }

  if (yearBlocks.length === 1 && pastBlocks.length >= 1) {
    const b = yearBlocks[0];
    return { ranges: [{ start: b.start, end: b.end }] };
  }

  return null;
};

/** Section C: full-width section rows in tbody (grey band) — must not receive FY column tint. */
const isSectionCGreyBannerRow = (row) => {
  if (!Array.isArray(row) || row.length !== 1) return false;
  const t = sanitizeText(row[0]?.content || "");
  if (/^(Permanent employees|Other than Permanent employees|Permanent workers|Other than Permanent workers)$/i.test(t)) return true;
  if (/^Total Permanent Employees$/i.test(t)) return true;
  if (/^Total Permanent Workers$/i.test(t)) return true;
  if (/^Total Employees$/i.test(t)) return true;
  if (/^Total Workers$/i.test(t)) return true;
  if (/^(Employees|Workers)$/i.test(t)) return true;
  return false;
};

const isSectionHeaderRow = (row) => {
  if (!Array.isArray(row) || row.length === 0) return false;
  const nonEmpty = row.filter((c) => sanitizeText(c?.content || "") !== "");
  if (nonEmpty.length !== 1) return false;
  const txt = sanitizeText(nonEmpty[0]?.content || "");
  if (!txt) return false;
  return /^[A-Z\s&/()-]+$/.test(txt);
};

/** Section B PDF: single-cell banner rows (title case) from convertSectionBUnifiedTableHtml. */
const isSectionBSubsectionBannerRow = (row) => {
  if (!Array.isArray(row) || row.length !== 1) return false;
  const txt = sanitizeText(row[0]?.content || "");
  if (!txt) return false;
  return /^Policy and management processes$/i.test(txt) || /^Governance, leadership and oversight$/i.test(txt);
};

const ensureSpace = (doc, y, requiredHeight = 12) => {
  if (y + requiredHeight <= PAGE.height - PAGE.marginBottom) return y;
  doc.addPage();
  return PAGE.marginTop;
};

const drawWrappedText = (doc, text, y, options = {}) => {
  const x = options.x ?? PAGE.marginLeft;
  const maxWidth = options.maxWidth ?? PAGE.width - PAGE.marginLeft - PAGE.marginRight;
  const fontSize = options.fontSize ?? 10;
  const lineGap = options.lineGap ?? 4.4;
  const fontStyle = options.fontStyle ?? "normal";

  doc.setFont("helvetica", fontStyle);
  doc.setFontSize(fontSize);
  const raw = pdfSafeMultiline(text);
  const paragraphs = raw.split(/\n/);
  let cursor = y;
  paragraphs.forEach((para) => {
    const cleaned = para.replace(/[ \t\r\f\v]+/g, " ").trim();
    if (!cleaned) {
      cursor += lineGap * 0.35;
      return;
    }
    const lines = doc.splitTextToSize(cleaned, maxWidth);
    lines.forEach((line) => {
      cursor = ensureSpace(doc, cursor, lineGap);
      doc.text(line, x, cursor);
      cursor += lineGap;
    });
  });
  return cursor;
};

const loadImageAsDataUrl = async (src) => {
  if (!src) return null;

  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth || image.width;
        canvas.height = image.naturalHeight || image.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(image, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = src;
  });
};

const LOGO_RENDER_HEIGHT = 18;
const LOGO_MAX_WIDTH = 53;

const loadLogoImage = async (src) => {
  if (!src) return null;

  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      try {
        const naturalWidth = image.naturalWidth || image.width;
        const naturalHeight = image.naturalHeight || image.height;
        const canvas = document.createElement("canvas");
        canvas.width = naturalWidth;
        canvas.height = naturalHeight;
        canvas.getContext("2d").drawImage(image, 0, 0);
        resolve({
          dataUrl: canvas.toDataURL("image/png"),
          naturalWidth,
          naturalHeight,
        });
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = src;
  });
};

const getLogoPdfDimensions = (naturalWidth, naturalHeight) => {
  const height = LOGO_RENDER_HEIGHT;
  if (!naturalWidth || !naturalHeight) {
    return { width: LOGO_MAX_WIDTH, height };
  }

  let width = height * (naturalWidth / naturalHeight);
  if (width > LOGO_MAX_WIDTH) {
    const scale = LOGO_MAX_WIDTH / width;
    return { width: LOGO_MAX_WIDTH, height: height * scale };
  }

  return { width, height };
};

const groupByModuleAndTitle = (reportData) => {
  const grouped = {};
  (reportData || []).forEach((item) => {
    const module = item?.module || "Section A";
    const title = item?.title || "General";
    if (!grouped[module]) grouped[module] = {};
    if (!grouped[module][title]) grouped[module][title] = [];
    grouped[module][title].push(item);
  });
  return grouped;
};

/** Roman numerals in parentheses, e.g. 24. (iii) … — used for sort order. */
const ROMAN_PAREN_ORDER = {
  i: 1,
  ii: 2,
  iii: 3,
  iv: 4,
  v: 5,
  vi: 6,
  vii: 7,
  viii: 8,
  ix: 9,
  x: 10,
  xi: 11,
  xii: 12,
};

const compareQuestionSubParts = (pa, pb) => {
  if (pa.sub && !pb.sub) return 1;
  if (!pa.sub && pb.sub) return -1;
  if (!pa.sub && !pb.sub) return 0;
  if (pa.subStyle === "romanParen" && pb.subStyle === "romanParen") {
    return (ROMAN_PAREN_ORDER[pa.sub] ?? 99) - (ROMAN_PAREN_ORDER[pb.sub] ?? 99);
  }
  if (pa.subStyle === "romanParen") return 1;
  if (pb.subStyle === "romanParen") return -1;
  return pa.sub.localeCompare(pb.sub);
};

/** Same prefix parsing as `pdf.js` for consistent ordering. */
const parseQuestionPrefix = (questionText) => {
  const q = (questionText || "").trim();
  const parenRom = q.match(/^(\d+)[\.\)]\s*\(([ivxlcdm]+)\)\s*(.*)$/i);
  if (parenRom) {
    return {
      number: parenRom[1],
      sub: parenRom[2].toLowerCase(),
      subStyle: "romanParen",
      body: parenRom[3] || "",
    };
  }
  const dotLetterSub = q.match(/^(\d+)[\.\)]\s+([a-z])[\.\)]\s*(.*)$/i);
  if (dotLetterSub) {
    return { number: dotLetterSub[1], sub: dotLetterSub[2].toLowerCase(), body: dotLetterSub[3] || "" };
  }
  const tightLetterSub = q.match(/^(\d+)[\.\)]([a-z])[\.\)]\s*(.*)$/i);
  if (tightLetterSub) {
    return { number: tightLetterSub[1], sub: tightLetterSub[2].toLowerCase(), body: tightLetterSub[3] || "" };
  }
  const subMatch = q.match(/^(\d+)\s*([a-zA-Z])[\.\)]?\s*(.*)$/);
  if (subMatch) {
    return { number: subMatch[1], sub: subMatch[2].toLowerCase(), body: subMatch[3] || "" };
  }
  const numberMatch = q.match(/^(\d+)[\.\)]?\s*(.*)$/);
  if (numberMatch) {
    return { number: numberMatch[1], sub: null, body: numberMatch[2] || "" };
  }
  return null;
};

/** Match Section C ordering: Essential Indicators first, then Leadership, then question number/sub. */
const sortQuestionsByPrefix = (questions) => {
  const list = questions || [];
  const hasSectionCType = list.some((q) => q?.questionType);

  if (!hasSectionCType) {
    return [...list].sort((a, b) => {
      const pa = parseQuestionPrefix(a?.question);
      const pb = parseQuestionPrefix(b?.question);
      if (!pa && !pb) return 0;
      if (!pa) return 1;
      if (!pb) return -1;
      const na = parseInt(pa.number, 10);
      const nb = parseInt(pb.number, 10);
      if (na !== nb) return na - nb;
      const subCmp = compareQuestionSubParts(pa, pb);
      if (subCmp !== 0) return subCmp;
      return 0;
    });
  }

  const questionTypeOrder = {
    "Essential Indicators": 0,
    "Leadership Indicators": 1,
  };

  return [...list].sort((a, b) => {
    const typeDiff = (questionTypeOrder[a?.questionType] ?? 99) - (questionTypeOrder[b?.questionType] ?? 99);
    if (typeDiff !== 0) return typeDiff;

    const pa = parseQuestionPrefix(a?.question);
    const pb = parseQuestionPrefix(b?.question);
    if (!pa && !pb) return 0;
    if (!pa) return 1;
    if (!pb) return -1;
    const na = parseInt(pa.number, 10);
    const nb = parseInt(pb.number, 10);
    if (na !== nb) return na - nb;
    const subCmp = compareQuestionSubParts(pa, pb);
    if (subCmp !== 0) return subCmp;
    return 0;
  });
};

/** Layout: number in left column, small gap, then body; answers/tables share the same left edge. */
const QUESTION_NUM_COL_MM = 8;
const QUESTION_NUM_GAP_MM = 1.8;
/** Section C: slightly tighter gap between question number and text (matches module layout). */
const QUESTION_NUM_GAP_SECTION_C_MM = 1.0;
const QUESTION_FONT_SIZE = 9.5;
const ANSWER_FONT_SIZE = 9.0;
const TABLE_FONT_SIZE = 8.4;

/** Section B per-principle: tight badge around P# + wrapped description (custom draw in willDrawCell). */
const BRSR_P_GAP_MM = 4;
/** Padding inside the green badge (mm). */
const BRSR_P_LABEL_PAD_H_MM = 1.2;
const BRSR_P_LABEL_PAD_V_MM = 0.55;
/** Vertical gap between principle rows (mm). 0 = no extra gap; overflow line count uses ceil(gap/lh) NBSP lines (0 when gap is 0). */
const BRSR_P_ROW_GAP_MM = 0;
/** Tight padding for expanded P1–P9 body rows (Section B PDF). */
const BRSR_PP_ANSWER_CELL_PAD = { top: 0.4, bottom: 0.4, left: 1.2, right: 1.4 };
const BRSR_PP_LEAD_CELL_PAD = { top: 1, bottom: 1, left: 1.8, right: 1.8 };
const BRSR_P_LABEL_BG = [220, 252, 231];
const BRSR_P_LABEL_FG = [21, 128, 61];
const BRSR_P_LABEL_BORDER = [187, 247, 208];

/** Line height (mm) used to align overflow line-count with drawBrsrPerPrincipleCellContent. */
const brsrPerPrincipleLineHeightMm = (doc, fontSize) => {
  const fs = fontSize || TABLE_FONT_SIZE;
  const sf = doc.internal.scaleFactor;
  return (fs / sf) * (doc.getLineHeightFactor?.() || 1.15);
};

/** Square badge size (mm) around bold P# — matches draw + overflow height. */
const measureBrsrPerPrincipleLabelBadge = (doc, label, fontSize) => {
  const fs = fontSize || TABLE_FONT_SIZE;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(fs);
  const text = sanitizeText(String(label || "").trim()) || "P";
  const tw = doc.getTextWidth(text);
  const lh = brsrPerPrincipleLineHeightMm(doc, fs);
  const innerW = tw + BRSR_P_LABEL_PAD_H_MM * 2;
  const innerH = lh + BRSR_P_LABEL_PAD_V_MM * 2;
  const side = Math.max(innerW, innerH);
  return { side, text, lh };
};

/**
 * Build line array for AutoTable height so it matches two-column draw (no full blank lines between rows;
 * optional filler lines when the badge is taller than the description block).
 */
const brsrPerPrincipleOverflowLines = (pdfDoc, textSpace, pRows, fontSize) => {
  const GAP = BRSR_P_GAP_MM;
  const fs = fontSize || TABLE_FONT_SIZE;
  const lh = brsrPerPrincipleLineHeightMm(pdfDoc, fs);
  const out = [];
  pdfDoc.setFont("helvetica", "normal");
  pdfDoc.setFontSize(fs);

  pRows.forEach((r, idx) => {
    if (idx > 0) {
      const gapLines = Math.ceil(BRSR_P_ROW_GAP_MM / lh);
      for (let g = 0; g < gapLines; g++) out.push("\u00A0");
    }
    const { side } = measureBrsrPerPrincipleLabelBadge(pdfDoc, r.label, fs);
    const descW = Math.max(40, textSpace - side - GAP);
    out.push("\u00A0");
    const lines = pdfDoc.splitTextToSize(sanitizeText(r.text || "") || "-", descW);
    lines.forEach((ln) => out.push(ln));
    const descH = lines.length * lh;
    const rowBlockH = Math.max(side, descH);
    const linesNeeded = Math.ceil(rowBlockH / lh);
    const used = 1 + lines.length;
    const extra = Math.max(0, linesNeeded - used);
    for (let k = 0; k < extra; k++) out.push("\u00A0");
  });
  return out;
};

const drawBrsrPerPrincipleCellContent = (doc, cell, rows) => {
  const GAP = BRSR_P_GAP_MM;
  const ROW_GAP = BRSR_P_ROW_GAP_MM;
  const fontSize = cell.styles.fontSize || TABLE_FONT_SIZE;
  const lineH = brsrPerPrincipleLineHeightMm(doc, fontSize);
  const textSpace = cell.width - cell.padding("horizontal");
  const x0 = cell.x + cell.padding("left");
  let labelTop = cell.y + cell.padding("top");

  rows.forEach((r, idx) => {
    if (idx > 0) labelTop += ROW_GAP;
    const t = sanitizeText(r.text || "") || "-";
    const { side, text: labelText, lh } = measureBrsrPerPrincipleLabelBadge(doc, r.label, fontSize);
    const descW = Math.max(40, textSpace - side - GAP);
    doc.setFillColor(...BRSR_P_LABEL_BG);
    doc.rect(x0, labelTop, side, side, "F");
    doc.setDrawColor(...BRSR_P_LABEL_BORDER);
    doc.setLineWidth(0.1);
    doc.rect(x0, labelTop, side, side, "S");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(fontSize);
    doc.setTextColor(...BRSR_P_LABEL_FG);
    const labelCy = labelTop + side / 2 + lh * 0.32;
    doc.text(labelText, x0 + side / 2, labelCy, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...THEME.text);
    const descLines = doc.splitTextToSize(t, descW);
    const xDesc = x0 + side + GAP;
    let descY = labelTop + lineH * 0.85;
    descLines.forEach((line) => {
      doc.text(line, xDesc, descY);
      descY += lineH;
    });
    const blockBottom = Math.max(labelTop + side, descLines.length ? descY : labelTop + lineH * 0.85);
    labelTop = blockBottom;
  });
};

const getQuestionNumGapMm = (moduleName) => (moduleName === "Section C" ? QUESTION_NUM_GAP_SECTION_C_MM : QUESTION_NUM_GAP_MM);
const QUESTION_SUB_INDENT_MM = 6;
/** Sub-part label (a., b.) column + gap before sub-question body text. */
const QUESTION_SUB_LABEL_COL_MM = 5.5;
const QUESTION_SUB_LABEL_COL_PAREN_MM = 11;
const QUESTION_SUB_LABEL_GAP_MM = 2.2;

/** Width reserved for a., (i), etc. — must match drawNumberedSubQuestion. */
const getSubLabelColumnWidthMm = (subLabel) => {
  const s = (subLabel || "").trim();
  if (!s) return QUESTION_SUB_LABEL_COL_MM;
  return s.includes("(") ? Math.max(QUESTION_SUB_LABEL_COL_MM, QUESTION_SUB_LABEL_COL_PAREN_MM) : QUESTION_SUB_LABEL_COL_MM;
};

/**
 * Split stored question text into label + body. No DB change required; optional explicit fields win.
 * Supports: "1. ...", "1. a. ...", "1 a) ...", "a. ...", "i. ...", "ii) ...".
 */
const parseQuestionForDisplay = (item) => {
  const explicitLabel = sanitizeText(item?.questionNumber ?? item?.questionLabel ?? "");
  const explicitBody = item?.questionBody != null ? String(item.questionBody) : null;
  if (explicitLabel && explicitBody != null) {
    return {
      kind: "main",
      mainLabel: explicitLabel,
      subLabel: "",
      body: String(explicitBody).trim(),
      extraIndent: 0,
    };
  }

  const q = sanitizeText(item?.question || "");
  if (!q) return { kind: "plain", mainLabel: "", subLabel: "", body: "", extraIndent: 0 };

  // Compact step4 style: "19b.", "20a." (no space between number and letter)
  let m = q.match(/^(\d+)([a-z])[\.\)]\s*(.*)$/i);
  if (m) {
    return {
      kind: "numbered_sub",
      mainLabel: `${m[1]}.`,
      subLabel: `${m[2].toLowerCase()}.`,
      body: (m[3] || "").trim(),
      extraIndent: 0,
    };
  }

  m = q.match(/^(\d+)[\.\)]\s+([a-z])[\.\)]\s*(.*)$/i);
  if (m) {
    return {
      kind: "numbered_sub",
      mainLabel: `${m[1]}.`,
      subLabel: `${m[2].toLowerCase()}.`,
      body: (m[3] || "").trim(),
      extraIndent: 0,
    };
  }

  m = q.match(/^(\d+)\s*([a-z])[\.\)]\s*(.*)$/i);
  if (m) {
    return {
      kind: "numbered_sub",
      mainLabel: `${m[1]}.`,
      subLabel: `${m[2].toLowerCase()}.`,
      body: (m[3] || "").trim(),
      extraIndent: 0,
    };
  }

  m = q.match(/^(\d+)\s*([a-z])\)\s*(.*)$/i);
  if (m) {
    return {
      kind: "numbered_sub",
      mainLabel: `${m[1]}.`,
      subLabel: `${m[2].toLowerCase()})`,
      body: (m[3] || "").trim(),
      extraIndent: 0,
    };
  }

  // 24. (i) / (ii) / (iii) … (before generic "N. body" match)
  m = q.match(/^(\d+)[\.\)]\s*\(([ivxlcdm]+)\)\s*(.*)$/i);
  if (m) {
    return {
      kind: "numbered_sub",
      mainLabel: `${m[1]}.`,
      subLabel: `(${m[2].toLowerCase()})`,
      body: (m[3] || "").trim(),
      extraIndent: 0,
    };
  }

  m = q.match(/^(i{1,3}|iv|v|vi{0,3}|ix|x{1,3})[\.\)]\s*(.*)$/i);
  if (m) {
    return {
      kind: "roman",
      mainLabel: `${m[1].toLowerCase()}.`,
      subLabel: "",
      body: (m[2] || "").trim(),
      extraIndent: QUESTION_SUB_INDENT_MM,
    };
  }

  m = q.match(/^([a-z])[\.\)]\s+(.*)$/i);
  if (m) {
    return {
      kind: "letter_only",
      mainLabel: `${m[1].toLowerCase()}.`,
      subLabel: "",
      body: (m[2] || "").trim(),
      extraIndent: QUESTION_SUB_INDENT_MM + 2,
    };
  }

  m = q.match(/^(\d+)[\.\)]\s*(.*)$/);
  if (m) {
    return {
      kind: "main",
      mainLabel: `${m[1]}.`,
      subLabel: "",
      body: (m[2] || "").trim(),
      extraIndent: 0,
    };
  }

  return { kind: "plain", mainLabel: "", subLabel: "", body: q, extraIndent: 0 };
};

/** Main number + a./b./c. in separate columns; body text starts after sub-label gap. */
const drawNumberedSubQuestion = (doc, y, display, ensureContentSpace, { showMainNumber, gapMm }) => {
  const lineGap = 4.8;
  const gap = typeof gapMm === "number" ? gapMm : QUESTION_NUM_GAP_MM;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(QUESTION_FONT_SIZE);

  const extraIndent = display.extraIndent || 0;
  const numLeft = PAGE.marginLeft + extraIndent;
  const textStart = numLeft + QUESTION_NUM_COL_MM + gap;
  const subLabel = (display.subLabel || "").trim();
  const subColW = getSubLabelColumnWidthMm(subLabel);
  const bodyStart = textStart + subColW + QUESTION_SUB_LABEL_GAP_MM;
  const bodyMaxW = PAGE.width - PAGE.marginRight - bodyStart;
  const body = (display.body || "").trim();

  if (!subLabel && !body && !(showMainNumber && display.mainLabel)) return y;

  const bodyLines = body ? doc.splitTextToSize(body, bodyMaxW) : [];

  if (bodyLines.length === 0) {
    let curY = ensureSpace(doc, y, lineGap + 4);
    if (showMainNumber && display.mainLabel) {
      doc.setTextColor(...THEME.brand);
      doc.text(display.mainLabel, numLeft, curY);
    }
    if (subLabel) {
      doc.setTextColor(...THEME.subQuestionLabel);
      doc.text(subLabel, textStart, curY);
    }
    return curY + lineGap;
  }

  let curY = ensureSpace(doc, y, lineGap + 4);
  if (showMainNumber && display.mainLabel) {
    doc.setTextColor(...THEME.brand);
    doc.text(display.mainLabel, numLeft, curY);
  }
  if (subLabel) {
    doc.setTextColor(...THEME.subQuestionLabel);
    doc.text(subLabel, textStart, curY);
  }
  doc.setTextColor(...THEME.subQuestionLabel);
  doc.text(bodyLines[0], bodyStart, curY);
  curY += lineGap;

  for (let i = 1; i < bodyLines.length; i++) {
    curY = ensureSpace(doc, curY, lineGap);
    doc.setTextColor(...THEME.subQuestionLabel);
    doc.text(bodyLines[i], bodyStart, curY);
    curY += lineGap;
  }
  return curY;
};

const drawQuestionHangingIndent = (doc, y, display, ensureContentSpace, options = {}) => {
  const gapMm = typeof options.gapMm === "number" ? options.gapMm : QUESTION_NUM_GAP_MM;
  const lineGap = 4.8;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(QUESTION_FONT_SIZE);
  doc.setTextColor(...THEME.brand);

  if (display.kind === "plain") {
    const y2 = ensureSpace(doc, y, 12);
    return drawWrappedText(doc, display.body, y2, {
      fontStyle: "bold",
      fontSize: QUESTION_FONT_SIZE,
      lineGap,
      x: PAGE.marginLeft + display.extraIndent,
      maxWidth: PAGE.width - PAGE.marginLeft - PAGE.marginRight - display.extraIndent,
    });
  }

  if (display.kind === "numbered_sub") {
    return drawNumberedSubQuestion(doc, y, display, ensureContentSpace, { showMainNumber: true, gapMm });
  }

  const extraIndent = display.extraIndent || 0;
  const numLeft = PAGE.marginLeft + extraIndent;
  const textStart = numLeft + QUESTION_NUM_COL_MM + gapMm;
  const maxW = PAGE.width - PAGE.marginRight - textStart;

  const textToWrap = (display.body || "").trim();
  const subPartGrey = display.kind === "roman" || display.kind === "letter_only";

  if (!display.mainLabel && !textToWrap) return y;

  if (display.mainLabel && !textToWrap) {
    const y2 = ensureSpace(doc, y, lineGap + 4);
    doc.setTextColor(...(subPartGrey ? THEME.subQuestionLabel : THEME.brand));
    doc.text(display.mainLabel, numLeft, y2);
    return y2 + lineGap;
  }

  const lines = doc.splitTextToSize(textToWrap || "", maxW);
  if (lines.length === 0) return y;

  let curY = ensureSpace(doc, y, lineGap + 4);

  if (display.mainLabel) {
    doc.setTextColor(...(subPartGrey ? THEME.subQuestionLabel : THEME.brand));
    doc.text(display.mainLabel, numLeft, curY);
  }
  doc.setTextColor(...(subPartGrey ? THEME.subQuestionLabel : THEME.brand));
  doc.text(lines[0], textStart, curY);
  curY += lineGap;

  for (let i = 1; i < lines.length; i++) {
    curY = ensureSpace(doc, curY, lineGap);
    doc.setTextColor(...(subPartGrey ? THEME.subQuestionLabel : THEME.brand));
    doc.text(lines[i], textStart, curY);
    curY += lineGap;
  }

  return curY;
};

/** Left edge and width for answers/tables so they line up with question body text. */
const getAnswerContentLayout = (display, moduleName) => {
  const gapMm = getQuestionNumGapMm(moduleName);
  if (!display) {
    return {
      contentLeft: PAGE.marginLeft,
      answerMaxWidth: PAGE.width - PAGE.marginLeft - PAGE.marginRight,
    };
  }
  if (display.kind === "plain") {
    const left = PAGE.marginLeft + (display.extraIndent || 0);
    return { contentLeft: left, answerMaxWidth: PAGE.width - PAGE.marginRight - left };
  }
  const extraIndent = display.extraIndent || 0;
  const textStart = PAGE.marginLeft + extraIndent + QUESTION_NUM_COL_MM + gapMm;
  // Sub-parts (a., (i), …): answers align with the question body column, not under the sub-label.
  if (display.kind === "numbered_sub") {
    const subColW = getSubLabelColumnWidthMm(display.subLabel);
    const bodyStart = textStart + subColW + QUESTION_SUB_LABEL_GAP_MM;
    return { contentLeft: bodyStart, answerMaxWidth: PAGE.width - PAGE.marginRight - bodyStart };
  }
  return { contentLeft: textStart, answerMaxWidth: PAGE.width - PAGE.marginRight - textStart };
};

const countConsecutiveNumberedSubSameMain = (sorted, startIdx) => {
  const item = sorted[startIdx];
  if (!item) return 0;
  const title = item?.title ?? "";
  const qType = item?.questionType ?? "";
  const stemKey = sanitizeText(item?.questionStem || "");
  const d0 = parseQuestionForDisplay(item);
  if (d0.kind !== "numbered_sub") return 0;
  let j = startIdx;
  while (j < sorted.length) {
    const it = sorted[j];
    if ((it?.title ?? "") !== title) break;
    if ((it?.questionType ?? "") !== qType) break;
    if (sanitizeText(it?.questionStem || "") !== stemKey) break;
    const dj = parseQuestionForDisplay(it);
    if (dj.kind !== "numbered_sub" || dj.mainLabel !== d0.mainLabel) break;
    j++;
  }
  return j - startIdx;
};

/**
 * Cluster items like 19.a / 19.b / 19.c (same title, type, main number) so the parent stem
 * and main number render once, then each sub-part with its answer.
 */
const buildQuestionRenderUnits = (questions) => {
  const sorted = sortQuestionsByPrefix(questions || []);
  const units = [];
  let i = 0;
  while (i < sorted.length) {
    const n = countConsecutiveNumberedSubSameMain(sorted, i);
    const stem = sanitizeText(sorted[i]?.questionStem || "");
    const d0 = parseQuestionForDisplay(sorted[i]);
    if (n >= 1 && d0.kind === "numbered_sub" && (stem || n >= 2)) {
      units.push({
        kind: "stemGroup",
        stem,
        mainLabel: d0.mainLabel,
        items: sorted.slice(i, i + n),
      });
      i += n;
    } else {
      units.push({ kind: "single", item: sorted[i] });
      i += 1;
    }
  }
  return units;
};

/** Section C: non-table answers with <div class="brsr-pdf-rich-lines"> and <p><strong>…</strong> — …</p> */
const renderBrsrPdfRichLines = (doc, y, html, contentLeft, answerMaxWidth, ensureContentSpace) => {
  const parser = new DOMParser();
  const docEl = parser.parseFromString(html, "text/html");
  const root = docEl.querySelector(".brsr-pdf-rich-lines");
  if (!root) return y;
  let cursor = y;
  const lineGap = 4.3;
  const fontSize = ANSWER_FONT_SIZE;

  Array.from(root.querySelectorAll("p")).forEach((p) => {
    const full = (p.textContent || "").trim();
    if (!full) return;
    const strong = p.querySelector(":scope > strong");
    if (strong && full === (strong.textContent || "").trim()) {
      cursor = ensureSpace(doc, cursor, lineGap + 6);
      doc.setTextColor(...THEME.text);
      cursor = drawWrappedText(doc, full, cursor, {
        fontStyle: "bold",
        fontSize,
        lineGap,
        x: contentLeft,
        maxWidth: answerMaxWidth,
      });
      cursor += lineGap * 0.4;
      return;
    }
    if (strong) {
      const label = (strong.textContent || "").trim();
      const idx = full.indexOf(label);
      const rest =
        idx >= 0
          ? full
              .slice(idx + label.length)
              .replace(/^[\s—–-]+/, "")
              .trim()
          : "";
      cursor = ensureSpace(doc, cursor, lineGap + 6);
      doc.setTextColor(...THEME.text);
      cursor = drawWrappedText(doc, label, cursor, {
        fontStyle: "bold",
        fontSize,
        lineGap,
        x: contentLeft,
        maxWidth: answerMaxWidth,
      });
      if (rest) {
        doc.setFont("helvetica", "normal");
        cursor = drawWrappedText(doc, rest, cursor, {
          fontSize,
          lineGap,
          x: contentLeft,
          maxWidth: answerMaxWidth,
        });
      }
      cursor += lineGap * 0.4;
      return;
    }
    cursor = ensureSpace(doc, cursor, lineGap + 6);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...THEME.text);
    cursor = drawWrappedText(doc, full, cursor, {
      fontSize,
      lineGap,
      x: contentLeft,
      maxWidth: answerMaxWidth,
    });
    cursor += lineGap * 0.4;
  });
  return cursor;
};

const renderAnswerContent = (doc, y, item, contentLeft, answerMaxWidth, ensureContentSpace) => {
  const answer = item?.answer || "";

  if (!answer || (typeof answer === "string" && sanitizeText(answer) === "")) {
    doc.setTextColor(...THEME.text);
    const y2 = drawWrappedText(doc, "-", y, {
      fontSize: ANSWER_FONT_SIZE,
      lineGap: 4.2,
      x: contentLeft,
      maxWidth: answerMaxWidth,
    });
    return y2 + 1;
  }

  if (typeof answer === "string" && hasBrsrPdfRichLines(answer)) {
    doc.setFont("helvetica", "normal");
    const y2 = renderBrsrPdfRichLines(doc, y, answer, contentLeft, answerMaxWidth, ensureContentSpace);
    return y2 + 2;
  }

  if (typeof answer === "string" && hasTable(answer)) {
    const isSectionC = item?.module === "Section C";
    const isSectionBCombined = item?.id === "SectionB-Combined";
    let yCursor = y;
    const fragments = splitHtmlFragments(answer);
    fragments.forEach((fragment) => {
      if (fragment.type === "text") {
        doc.setTextColor(...THEME.text);
        yCursor = drawWrappedText(doc, fragment.content, yCursor, {
          fontSize: ANSWER_FONT_SIZE,
          lineGap: 4.2,
          x: contentLeft,
          maxWidth: answerMaxWidth,
        });
        yCursor += 1;
        return;
      }

      const { headRows, bodyRows } = tableElementToAutoTableInput(fragment.element);
      const expandedBodyRows = isSectionBCombined ? expandBrsrPerPrincipleBodyRows(bodyRows) : bodyRows;
      const numericColumns = detectNumericColumns(headRows, expandedBodyRows);
      const latestFyRange = getLatestFyColumnRange(headRows);
      const columnStyles = {};
      numericColumns.forEach((col) => {
        columnStyles[col] = { halign: "center" };
      });
      const mergedColumnStyles = { ...columnStyles };
      if (isSectionBCombined) {
        const usableWidth = PAGE.width - PAGE.marginLeft - PAGE.marginRight;
        const pColWidth = (usableWidth - 52) / 18; // 52mm for subject/disclosure col, rest split across 18 P-columns
        mergedColumnStyles[0] = { valign: "top", cellWidth: 52, halign: "left" };
        for (let i = 1; i <= 18; i++) {
          mergedColumnStyles[i] = { valign: "top", cellWidth: pColWidth, halign: "center" };
        }
      }
      // Principle 6 Essential Q1 (energy): autotable otherwise gives almost all width to the Parameter column.
      const isP6Q1EnergyConsumptionTable = item?.id === "SectionC-P6-Essential-q1_energyConsumption" && headRows?.[0]?.length === 3;
      if (isP6Q1EnergyConsumptionTable) {
        const tableInnerWidthMm = PAGE.width - contentLeft - PAGE.marginRight;
        const paramW = Math.min(100, Math.max(68, tableInnerWidthMm * 0.44));
        const fyW = (tableInnerWidthMm - paramW) / 2;
        mergedColumnStyles[0] = { ...mergedColumnStyles[0], valign: "top", cellWidth: paramW };
        mergedColumnStyles[1] = { ...mergedColumnStyles[1], valign: "top", cellWidth: fyW, halign: "center" };
        mergedColumnStyles[2] = { ...mergedColumnStyles[2], valign: "top", cellWidth: fyW, halign: "center" };
      }
      autoTable(doc, {
        startY: yCursor,
        margin: { left: contentLeft, right: PAGE.marginRight },
        head: headRows,
        body: expandedBodyRows,
        theme: "grid",
        rowPageBreak: "auto",
        showHead: "everyPage",
        styles: {
          font: "helvetica",
          fontSize: TABLE_FONT_SIZE,
          cellPadding: { top: 2, right: 1.8, bottom: 2, left: 1.8 },
          overflow: "linebreak",
          lineColor: THEME.tableLine,
          lineWidth: { top: 0.2, right: 0, bottom: 0.2, left: 0 },
          valign: "top",
          halign: "left",
        },
        headStyles: {
          fillColor: THEME.tableHeaderBg,
          textColor: THEME.dark,
          fontStyle: "bold",
          halign: "left",
          valign: "middle",
        },
        bodyStyles: {
          textColor: THEME.text,
          lineWidth: { top: 0.2, right: 0, bottom: 0.2, left: 0 },
          halign: "left",
          valign: "top",
        },
        columnStyles: mergedColumnStyles,
        willDrawCell: (hookData) => {
          const raw = hookData.cell.raw;
          if (!raw?._brsrPerPrincipleRows?.length) return;
          const pdf = doc;
          const bx = hookData.cell.x;
          const by = hookData.cell.y;
          const bw = hookData.cell.width;
          const bh = hookData.cell.height;
          const bi = raw._brsrPPBlockIndex;
          const bn = raw._brsrPPBlockSize;
          pdf.setDrawColor(...THEME.tableLine);
          pdf.setLineWidth(0.1);
          if (bi == null || bn == null || bn <= 1) {
            pdf.rect(bx, by, bw, bh, "S");
          } else {
            pdf.line(bx, by, bx, by + bh);
            pdf.line(bx + bw, by, bx + bw, by + bh);
            if (bi === 0) {
              pdf.line(bx, by, bx + bw, by);
            }
            if (bi === bn - 1) {
              pdf.line(bx, by + bh, bx + bw, by + bh);
            }
          }
          drawBrsrPerPrincipleCellContent(pdf, hookData.cell, raw._brsrPerPrincipleRows);
          return false;
        },
        didParseCell: (hookData) => {
          const rawText = sanitizeText(String(hookData.cell?.raw?.content || hookData.cell?.text?.join(" ") || ""));
          if (/^total\b/i.test(rawText)) {
            hookData.cell.styles.fontStyle = "bold";
          }

          const row = hookData.row?.raw || [];
          const rowLabel = sanitizeText(row?.[0]?.content || "");
          const currentCol = getCellColumnStart(row, hookData.column.index);
          const cellSpan = Math.max(1, hookData.cell?.raw?.colSpan || 1);
          const cellEnd = currentCol + cellSpan - 1;
          const isTotalNoOfAffectedRow = /^total\s+no\s+of\s+affected\s+employees\/?workers$/i.test(rowLabel) || /^total\s+no\s+of\s+affected\s+employees\/workers$/i.test(rowLabel);
          const skipFyTint = hookData.section === "body" && (isSectionCGreyBannerRow(row) || isTotalNoOfAffectedRow || (row.length === 1 && cellSpan >= 8));
          const inLatestFyRange = !skipFyTint && latestFyRange && Array.isArray(latestFyRange.ranges) && latestFyRange.ranges.some((r) => currentCol >= r.start && cellEnd <= r.end);

          if (isSectionC && hookData.section === "head") {
            hookData.cell.styles.fillColor = THEME.tableHeaderGreen;
            hookData.cell.styles.textColor = THEME.dark;
          }

          if (inLatestFyRange) {
            hookData.cell.styles.fillColor = THEME.latestFyBg;
            hookData.cell.styles.textColor = THEME.latestFyText;
            hookData.cell.styles.lineWidth = { top: 0.2, right: 0.2, bottom: 0.2, left: 0.2 };
          }

          if (hookData.section === "body" && isSectionCGreyBannerRow(row)) {
            hookData.cell.styles.fillColor = THEME.sectionRowBg;
            hookData.cell.styles.fontStyle = "bold";
            hookData.cell.styles.halign = "center";
            hookData.cell.styles.textColor = THEME.dark;
          }

          if (hookData.section === "body" && isSectionHeaderRow(row)) {
            hookData.cell.styles.fillColor = THEME.sectionRowBg;
            hookData.cell.styles.fontStyle = "bold";
            hookData.cell.styles.halign = "center";
            hookData.cell.styles.textColor = THEME.dark;
          }

          if (hookData.section === "body" && isSectionBSubsectionBannerRow(row)) {
            hookData.cell.styles.fillColor = THEME.sectionRowBg;
            hookData.cell.styles.fontStyle = "bold";
            hookData.cell.styles.halign = "center";
            hookData.cell.styles.textColor = THEME.dark;
          }

          // Section B: merged answer cells use columnStyles halign:center — that breaks text wrapping in Edge/print; keep Yes/No cells centered (colspan 2).
          if (isSectionBCombined && hookData.section === "body") {
            const colIdx = hookData.column?.index ?? 0;
            const span = Math.max(1, hookData.cell?.raw?.colSpan || 1);
            const brsrRaw = hookData.cell.raw;

            if (brsrRaw?._brsrSectionBHeaderRow || brsrRaw?._isThCell) {
              hookData.cell.styles.fillColor = THEME.tableHeaderBg;
              hookData.cell.styles.textColor = THEME.dark;
              hookData.cell.styles.fontStyle = "bold";
              hookData.cell.styles.valign = "middle";
              if (span >= 19) {
                hookData.cell.styles.halign = "left";
              } else if (span >= 2) {
                hookData.cell.styles.halign = "center";
              } else {
                hookData.cell.styles.halign = "left";
              }
            } else if (colIdx === 0 && span === 1 && !brsrRaw?._brsrPerPrincipleRows?.length && !isSectionBSubsectionBannerRow(row)) {
              hookData.cell.styles.fontStyle = "bold";
            }

            if (!(brsrRaw?._brsrSectionBHeaderRow || brsrRaw?._isThCell) && colIdx >= 1 && span >= 9) {
              hookData.cell.styles.halign = "left";
            }
            if (brsrRaw?._brsrPPBlockLead) {
              hookData.cell.styles.valign = "top";
              hookData.cell.styles.halign = "left";
              hookData.cell.styles.cellPadding = { ...BRSR_PP_LEAD_CELL_PAD };
            }
            if (brsrRaw?._brsrPerPrincipleRows?.length) {
              const pRows = brsrRaw._brsrPerPrincipleRows;
              hookData.cell.styles.valign = "top";
              hookData.cell.styles.halign = "left";
              hookData.cell.styles.fillColor = [255, 255, 255];
              hookData.cell.styles.cellPadding = { ...BRSR_PP_ANSWER_CELL_PAD };
              const bi = brsrRaw._brsrPPBlockIndex;
              const bn = brsrRaw._brsrPPBlockSize;
              if (bi != null && bn != null && bn > 1) {
                hookData.cell.styles.lineWidth = { top: 0, bottom: 0, left: 0, right: 0 };
              }
              hookData.cell.styles.overflow = (text, textSpace) => {
                const fs = hookData.cell.styles.fontSize || TABLE_FONT_SIZE;
                return brsrPerPrincipleOverflowLines(doc, textSpace, pRows, fs);
              };
            }
          }

          // Section C P1 Q2: Monetary / Non-Monetary band rows (HTML inline bg may not reach autotable)
          if (hookData.section === "body" && row.length === 1 && cellSpan >= 5 && (rawText === "Monetary" || rawText === "Non-Monetary")) {
            hookData.cell.styles.fillColor = [245, 245, 245];
            hookData.cell.styles.fontStyle = "bold";
            hookData.cell.styles.textColor = THEME.dark;
            hookData.cell.styles.halign = "left";
          }
        },
      });
      const gapAfterTable = isSectionBCombined ? 1.5 : 6;
      yCursor = (doc.lastAutoTable?.finalY || yCursor) + gapAfterTable;
    });
    return yCursor;
  }

  const textAnswer = typeof answer === "string" ? stripHtmlToText(answer) : String(answer);
  doc.setTextColor(...THEME.text);
  const y2 = drawWrappedText(doc, textAnswer || "-", y, {
    fontSize: ANSWER_FONT_SIZE,
    lineGap: 4.3,
    x: contentLeft,
    maxWidth: answerMaxWidth,
  });
  return y2 + 2;
};

const drawCoverPage = (doc, { reportTitle, reportingYear, logoImage, coverBgDataUrl }) => {
  if (coverBgDataUrl) {
    try {
      doc.addImage(coverBgDataUrl, "JPEG", 0, 0, PAGE.width, PAGE.height, undefined, "FAST");
    } catch {
      doc.setFillColor(...THEME.pageBg);
      doc.rect(0, 0, PAGE.width, PAGE.height, "F");
    }
  } else {
    doc.setFillColor(...THEME.pageBg);
    doc.rect(0, 0, PAGE.width, PAGE.height, "F");
  }

  if (logoImage?.dataUrl) {
    try {
      const { width, height } = getLogoPdfDimensions(logoImage.naturalWidth, logoImage.naturalHeight);
      doc.addImage(logoImage.dataUrl, "PNG", 14, 14, width, height, undefined, "FAST");
    } catch {
      // No-op.
    }
  }

  doc.setTextColor(...THEME.dark);
  doc.setFont("helvetica", "bold");
  // Mirror CSRD cover title/year positioning near lower-left.
  doc.setFontSize(18);
  doc.text(reportingYear || "-", 14, 236);
  doc.setFontSize(34);
  doc.text(reportTitle || "BRSR Report", 14, 252, { maxWidth: 165 });
};

const drawIndexPage = (doc, indexPageNumber, moduleStartPages) => {
  doc.setPage(indexPageNumber);
  doc.setFillColor(...THEME.pageBg);
  doc.rect(0, 0, PAGE.width, PAGE.height, "F");

  doc.setTextColor(...THEME.brand);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Index", PAGE.width / 2, 22, { align: "center" });

  let y = 34;
  MODULE_ORDER.forEach((moduleName) => {
    const pageNo = moduleStartPages[moduleName];
    if (!pageNo) return;
    const displayPageNo = Math.max(1, pageNo - 2);
    const label = MODULE_DISPLAY[moduleName] || moduleName;
    doc.setTextColor(...THEME.dark);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.5);
    // Clickable index rows (same pattern as CSRD PDF): jump to section start page.
    doc.textWithLink(label, PAGE.marginLeft, y, { maxWidth: 160, pageNumber: pageNo });
    doc.textWithLink(String(displayPageNo), PAGE.width - PAGE.marginRight, y, { align: "right", pageNumber: pageNo });
    doc.setDrawColor(...THEME.tableLine);
    doc.line(PAGE.marginLeft, y + 2, PAGE.width - PAGE.marginRight, y + 2);
    y += 10;
  });
};

/** Footer page numbers: distinct from table grid (matches CSRD PDF). */
const FOOTER_PAGE_TEXT_Y = PAGE.height - 7;

const drawPageFooters = (doc) => {
  const total = doc.getNumberOfPages();
  for (let pageNo = 3; pageNo <= total; pageNo++) {
    doc.setPage(pageNo);
    doc.setTextColor(...THEME.muted);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7.5);
    const bodyPageLabel = pageNo - 2;
    doc.text(`Page ${bodyPageLabel}`, PAGE.width - PAGE.marginRight, FOOTER_PAGE_TEXT_Y, { align: "right" });
    doc.setFont("helvetica", "normal");
  }
};

export const generateBRSRPdfBlob = async ({ reportData, reportTitle, reportingYear, companyLogo }) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
  const grouped = groupByModuleAndTitle(reportData);
  const logoImage = await loadLogoImage(companyLogo);
  const coverBgDataUrl = await loadImageAsDataUrl(COVER_IMAGE_SRC);

  // Page 1: cover
  drawCoverPage(doc, { reportTitle, reportingYear, logoImage, coverBgDataUrl });
  // Page 2: index placeholder (filled at end)
  doc.addPage();
  // Page 3+: content
  doc.addPage();

  const moduleStartPages = {};
  let y = PAGE.marginTop;

  const ensureContentSpace = (requiredHeight = 12) => {
    y = ensureSpace(doc, y, requiredHeight);
  };

  MODULE_ORDER.forEach((moduleName) => {
    const moduleData = grouped[moduleName];
    if (!moduleData) return;

    // Force each section to start on a new page.
    if (moduleName !== MODULE_ORDER[0]) {
      doc.addPage();
      y = PAGE.marginTop;
    }

    moduleStartPages[moduleName] = moduleStartPages[moduleName] || doc.getCurrentPageInfo().pageNumber;
    ensureContentSpace(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...THEME.brand);
    doc.setFontSize(14);
    doc.text(MODULE_DISPLAY[moduleName] || moduleName, PAGE.marginLeft, y);
    y += 10;

    Object.keys(moduleData).forEach((title) => {
      const questions = moduleData[title] || [];

      ensureContentSpace(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...THEME.dark);
      doc.setFontSize(12);
      y = drawWrappedText(doc, title, y, { fontStyle: "bold", fontSize: 11.5, lineGap: 5.2 });
      y += 2.5;

      const renderUnits = buildQuestionRenderUnits(questions);
      let lastRenderedQuestionType = null;

      renderUnits.forEach((unit) => {
        const renderQuestionTypeBar = (qTypeRaw) => {
          const qType = sanitizeText(qTypeRaw || "");
          if (!qType || qType === lastRenderedQuestionType) return;
          lastRenderedQuestionType = qType;
          y += 4;
          ensureContentSpace(20);
          const barTop = y;
          const barH = 10;
          doc.setFillColor(...THEME.questionTypeBg);
          doc.rect(PAGE.marginLeft, barTop, PAGE.width - PAGE.marginLeft - PAGE.marginRight, barH, "F");
          doc.setTextColor(...THEME.dark);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(13);
          doc.text(qType, PAGE.marginLeft + 2, barTop + 7.2);
          y = barTop + barH + 5;
        };

        if (unit.kind === "single") {
          const item = unit.item;
          const question = sanitizeText(item?.question || "");
          renderQuestionTypeBar(item?.questionType);

          const qDisplay = parseQuestionForDisplay(item);
          const gapMm = getQuestionNumGapMm(moduleName);
          const { contentLeft, answerMaxWidth } = getAnswerContentLayout(qDisplay, moduleName);

          if (question) {
            y += 2.5;
            ensureContentSpace(10);
            y = drawQuestionHangingIndent(doc, y, qDisplay, ensureContentSpace, { gapMm });
            y += 1.2;
          }

          y = renderAnswerContent(doc, y, item, contentLeft, answerMaxWidth, ensureContentSpace);
          return;
        }

        // Grouped main number + stem once, then a./b./c. each with answer (and gap after letters).
        const groupItems = unit.items;
        const first = groupItems[0];
        renderQuestionTypeBar(first?.questionType);

        y += 2.5;
        ensureContentSpace(14);
        const gapMm = getQuestionNumGapMm(moduleName);
        const stemDisplay = {
          kind: "main",
          mainLabel: unit.mainLabel,
          subLabel: "",
          body: unit.stem,
          extraIndent: 0,
        };
        y = drawQuestionHangingIndent(doc, y, stemDisplay, ensureContentSpace, { gapMm });
        y += 1.5;

        groupItems.forEach((subItem, idx) => {
          if (idx > 0) y += 2;
          const d = parseQuestionForDisplay(subItem);
          ensureContentSpace(10);
          y = drawNumberedSubQuestion(doc, y, d, ensureContentSpace, { showMainNumber: false, gapMm });
          y += 0.6;
          const { contentLeft, answerMaxWidth } = getAnswerContentLayout(d, moduleName);
          y = renderAnswerContent(doc, y, subItem, contentLeft, answerMaxWidth, ensureContentSpace);
        });
      });

      y += 4;
    });
  });

  drawIndexPage(doc, 2, moduleStartPages);

  // Last page with same full-cover image as CSRD. Only add it when the image is
  // available — otherwise it renders as a blank trailing page.
  if (coverBgDataUrl) {
    doc.addPage();
    try {
      doc.addImage(coverBgDataUrl, "JPEG", 0, 0, PAGE.width, PAGE.height, undefined, "FAST");
    } catch {
      doc.setFillColor(...THEME.pageBg);
      doc.rect(0, 0, PAGE.width, PAGE.height, "F");
    }
  }

  drawPageFooters(doc);

  return doc.output("blob");
};
